import type {
  AppConfig,
  Block,
  CalendarColor,
  CalendarFeed,
  FeedFetch,
  FeedCategory,
  FeedValidators,
  FindReplaceRule,
  FontSize,
  Haptics,
  MatchPosition,
  Motion,
  Palette,
  ParsedEvent,
  Scheme,
  SettingsSections,
  Spacing,
  StyleVariant,
  SubLanes,
  TraySide,
  NextEvents,
} from './types';
import { BLOCK_OPTIONS, CALENDAR_COLORS, DEFAULT_FONT_SIZE, FONT_SIZES, FEED_CATEGORIES, FEED_FETCH_OPTIONS, MATCH_POSITIONS, NEXT_EVENTS_OPTIONS, PALETTES, SCHEMA_VERSION, SCHEMES, SCRATCHPAD_FEED_ID, SETTINGS_SECTION_IDS, SUB_LANE_OPTIONS } from './types';
import { offsetMinutes, resolveLocalTz } from './format';
import { deserializeScratchEvents, serializeScratchEvents } from './scratchpad';
import { isValidTimezone } from './recurrence';
import { isPinHash } from './kiosk-pin';

const VALID_STYLES: StyleVariant[] = [
  'none', 'outline', 'bold', 'inverted', 'dashed', 'muted', 'striked', 'hidden',
];

export const STORAGE_KEY = 'calendar-timeline:config';

export const GREEK_HOLIDAYS_URL =
  'https://calendar.google.com/calendar/ical/en.greek%23holiday%40group.v.calendar.google.com/public/basic.ics';
export const USA_HOLIDAYS_URL =
  'https://calendar.google.com/calendar/ical/en.usa%23holiday%40group.v.calendar.google.com/public/basic.ics';

export const REFRESH_INTERVAL_OPTIONS = [
  30 * 60 * 1000,
  60 * 60 * 1000,
  240 * 60 * 1000,
] as const;

export function snapRefreshInterval(ms: number): number {
  let best = REFRESH_INTERVAL_OPTIONS[0];
  let bestDelta = Math.abs(ms - best);
  for (const v of REFRESH_INTERVAL_OPTIONS) {
    const d = Math.abs(ms - v);
    if (d < bestDelta) { best = v; bestDelta = d; }
  }
  return best;
}

export const DEFAULT_RULES: FindReplaceRule[] = [
  // Canceled first: rule precedence is first-wins (see decorate() in rules.ts), so
  // a struck-through CANCELED event overrides any later style rule it also matches.
  // These are matte (no replace) — they only apply a style/block, never rewrite text.
  { id: 'default-canceled', find: 'CANCELED', style: 'striked', category: 'none' },
  { id: 'default-tbd', find: 'TBD', style: 'dashed', category: 'none' },
  { id: 'default-tbc', find: 'TBC', style: 'dashed', category: 'none' },
  { id: 'default-observance', find: 'Observance', style: 'dashed', category: 'observances', block: 'local' },
];

export const DEFAULT_RULE_IDS: ReadonlySet<string> = new Set(DEFAULT_RULES.map((r) => r.id));

function hoursFrom(tz: string): number {
  return ((offsetMinutes(resolveLocalTz()) ?? 0) - (offsetMinutes(tz) ?? 0)) / 60;
}

export type HolidayPrimary = 'greek' | 'usa';

export function defaultPrimaryHoliday(): HolidayPrimary {
  const athens = Math.abs(hoursFrom('Europe/Athens'));
  if (athens <= 1) return 'greek';
  const newYork = Math.abs(hoursFrom('America/New_York'));
  if (newYork <= 2) return 'usa';
  return 'greek';
}

export function scratchpadFeed(order: number): CalendarFeed {
  return {
    id: SCRATCHPAD_FEED_ID,
    source: { kind: 'scratchpad' },
    name: 'Draft',
    collapsed: false,
    order,
    kind: 'events',
    category: 'none',
    hidden: true,
  };
}

// The feed ids present in a fresh default config: the two seeded holiday feeds
// plus the Draft lane. Used to detect an un-customized install.
export const DEFAULT_FEED_IDS: ReadonlySet<string> = new Set([
  'user:greek-bank-holidays',
  'user:usa-bank-holidays',
  SCRATCHPAD_FEED_ID,
]);

// True when the user hasn't added any feed of their own — every lane is a default
// (the seeded holidays + Draft). Lets a share import apply directly without the
// merge prompt on a fresh recipient.
export function isDefaultOnlyFeeds(feeds: CalendarFeed[]): boolean {
  return feeds.every((f) => DEFAULT_FEED_IDS.has(f.id));
}

// `base` (a feed's URL-hash id), or `base-2`, `base-3`… when another feed holds
// it: ids don't follow URLs (seeded feeds have readable ids, an edited URL keeps
// its id), so a URL's hash can already be taken.
export function uniqueFeedId(base: string, feeds: readonly { id: string }[]): string {
  const taken = new Set(feeds.map((f) => f.id));
  let id = base;
  for (let n = 2; taken.has(id); n++) id = base + '-' + n;
  return id;
}

// True while the rules are the shipped defaults, unedited (none added).
// Compared normalized (a loaded rule carries normalizeRule's defaults), with
// sorted keys so field order doesn't matter.
const ruleKey = (r: FindReplaceRule): string => {
  const n = normalizeRule(r) as Record<string, unknown>;
  return JSON.stringify(Object.keys(n).sort().map((k) => [k, n[k]]));
};
let defaultRuleKeys: Map<string, string> | null = null;
export function isDefaultOnlyRules(rules: FindReplaceRule[]): boolean {
  defaultRuleKeys ??= new Map(DEFAULT_RULES.map((r) => [r.id, ruleKey(r)]));
  return rules.every((r) => defaultRuleKeys!.get(r.id) === ruleKey(r));
}

export function defaultConfig(): AppConfig {
  const primary = defaultPrimaryHoliday();
  const greekIsPrimary = primary === 'greek';
  const greek: CalendarFeed = {
    id: 'user:greek-bank-holidays',
    source: { kind: 'user', url: GREEK_HOLIDAYS_URL },
    name: 'Greek Public Holidays',
    collapsed: false,
    order: greekIsPrimary ? 0 : 1,
    kind: greekIsPrimary ? 'holidays' : 'events',
    category: greekIsPrimary ? 'holidays' : 'observances',
    // Block is independent of Type — set it explicitly so a fresh install shows
    // the primary holidays as a full-width band and the secondary as a row hatch.
    block: greekIsPrimary ? 'global' : 'local',
  };
  const usa: CalendarFeed = {
    id: 'user:usa-bank-holidays',
    source: { kind: 'user', url: USA_HOLIDAYS_URL },
    name: 'USA Public Holidays',
    collapsed: false,
    order: greekIsPrimary ? 1 : 0,
    kind: greekIsPrimary ? 'events' : 'holidays',
    category: greekIsPrimary ? 'observances' : 'holidays',
    block: greekIsPrimary ? 'local' : 'global',
  };
  return {
    feeds: [greek, usa, scratchpadFeed(2)],
    refreshIntervalMs: 60 * 60 * 1000,
    schemaVersion: SCHEMA_VERSION,
    scheme: 'auto',
    palette: 'auto',
    motion: 'auto',
    spacing: 'auto',
    traySide: 'auto',
    feedFetch: 'auto',
    nextEvents: 'all',
    borderWeight: 'thin',
    haptics: 'auto',
    fontSize: DEFAULT_FONT_SIZE,
    locale: 'en',
    dateFormat: 'YYYY-MM-DD',
    rules: DEFAULT_RULES.map((r) => ({ ...r })),
    cardShowDescription: false,
    cardShowLocation: true,
    timezone: 'local',
    dst: 'auto',
    timeFormat: '24h',
    weekStart: 'monday',
    timezone1: 'Europe/Athens',
    timezone2: 'America/New_York',
    weekHourScale: 1,
    pastMonths: 12,
    futureMonths: 24,
    morningLimit: '08:30',
    eveningLimit: '20:30',
    trayFilter: {
      categories: [
        'none', 'events', 'holidays', 'observances', 'operations', 'announcements', 'guests',
        'travel-local', 'travel-international',
      ],
    },
    kioskPin: null,
  };
}

function normalizeScheme(value: unknown): Scheme {
  return SCHEMES.includes(value as Scheme) ? (value as Scheme) : 'auto';
}

// Falls back to 'auto' (follow the app default) rather than to a concrete flavor,
// so a config that never expressed a preference doesn't get pinned to one. A
// config that does store a flavor passes through untouched.
function normalizePalette(value: unknown): Palette {
  return PALETTES.includes(value as Palette) ? (value as Palette) : 'auto';
}

function normalizeMotion(value: unknown): Motion {
  if (value === 'auto' || value === 'reduced' || value === 'full') return value;
  return 'auto';
}

function normalizeSpacing(value: unknown): Spacing {
  if (value === 'auto' || value === 'condensed' || value === 'relaxed') return value;
  return 'auto';
}

// Configs saved before the setting existed showed every event.
function normalizeNextEvents(value: unknown): NextEvents {
  return (NEXT_EVENTS_OPTIONS as readonly unknown[]).includes(value) ? (value as NextEvents) : 'all';
}

function normalizeTraySide(value: unknown): TraySide {
  if (value === 'auto' || value === 'bottom' || value === 'left') return value;
  return 'auto';
}

function normalizeHaptics(value: unknown): Haptics {
  if (
    value === 'auto' ||
    value === 'sound' ||
    value === 'vibration' ||
    value === 'both' ||
    value === 'off'
  ) {
    return value;
  }
  return 'auto';
}

function normalizeFontSize(value: unknown): FontSize {
  return FONT_SIZES.includes(value as FontSize) ? (value as FontSize) : DEFAULT_FONT_SIZE;
}

function normalizeFeed(raw: unknown, fallbackOrder: number): CalendarFeed | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  if (typeof f.id !== 'string' || typeof f.name !== 'string') return null;
  if (!f.source || typeof f.source !== 'object') return null;
  const source = f.source as Record<string, unknown>;
  let normalizedSource: CalendarFeed['source'] | null = null;
  if (source.kind === 'user' && typeof source.url === 'string') {
    normalizedSource = { kind: 'user', url: source.url };
  } else if (source.kind === 'secret' && typeof source.id === 'string') {
    normalizedSource = { kind: 'secret', id: source.id };
  } else if (source.kind === 'scratchpad') {
    normalizedSource = { kind: 'scratchpad', id: typeof source.id === 'string' ? source.id : 'default' };
  }
  if (!normalizedSource) return null;
  // Only the built-in Draft lane has a fixed name; imported local lanes keep theirs.
  const isDraftLane =
    normalizedSource.kind === 'scratchpad' && (normalizedSource.id ?? 'default') === 'default';
  const color: CalendarColor | undefined =
    typeof f.color === 'string' && (CALENDAR_COLORS as string[]).includes(f.color)
      ? (f.color as CalendarColor)
      : undefined;
  const style: StyleVariant | undefined =
    typeof f.style === 'string' && (VALID_STYLES as string[]).includes(f.style)
      ? (f.style as StyleVariant)
      : undefined;
  const kind: 'events' | 'holidays' = f.kind === 'holidays' ? 'holidays' : 'events';
  let category: FeedCategory =
    typeof f.category === 'string' && (FEED_CATEGORIES as string[]).includes(f.category)
      ? (f.category as FeedCategory)
      : kind === 'holidays'
        ? 'holidays'
        : 'none';
  // Legacy (pre-merge) configs carried a separate `travel` tag; fold it back into
  // the event type. A present travel tag is the more specific label, so it wins.
  if (f.travel === 'international') category = 'travel-international';
  else if (f.travel === 'local') category = 'travel-local';
  // Block is fully independent of Type — it only ever comes from an explicit
  // block value, never derived from the holidays/observances category.
  const block: Block =
    typeof f.block === 'string' && (BLOCK_OPTIONS as string[]).includes(f.block)
      ? (f.block as Block)
      : 'none';
  const timezone =
    typeof f.timezone === 'string' && f.timezone.trim().length > 0
      ? f.timezone.trim()
      : undefined;
  return {
    id: f.id,
    source: normalizedSource,
    // The Draft (scratchpad) feed is a system feed with a fixed name; coerce
    // the legacy "Scratchpad" name to "Draft". Imported local lanes keep theirs.
    name: isDraftLane ? 'Draft' : f.name,
    collapsed: f.collapsed === true,
    order: typeof f.order === 'number' ? f.order : fallbackOrder,
    kind: category === 'holidays' ? 'holidays' : 'events',
    category,
    ...(block !== 'none' ? { block } : {}),
    ...(color ? { color } : {}),
    ...(style ? { style } : {}),
    ...(timezone ? { timezone } : {}),
    ...(f.hidden === true ? { hidden: true } : {}),
    // Added after schema 3; older configs have no field and stay packed.
    ...(typeof f.subLanes === 'string' && f.subLanes !== 'none' && (SUB_LANE_OPTIONS as readonly string[]).includes(f.subLanes)
      ? { subLanes: f.subLanes as SubLanes }
      : {}),
  };
}

function normalizeRule(r: FindReplaceRule): FindReplaceRule {
  const style: StyleVariant =
    typeof r.style === 'string' && (VALID_STYLES as string[]).includes(r.style)
      ? r.style
      : 'none';
  const category: FeedCategory =
    typeof r.category === 'string' && (FEED_CATEGORIES as string[]).includes(r.category)
      ? r.category
      : 'none';
  const color: CalendarColor | undefined =
    typeof r.color === 'string' && (CALENDAR_COLORS as string[]).includes(r.color)
      ? r.color
      : undefined;
  const block: Block | undefined =
    typeof r.block === 'string' && (BLOCK_OPTIONS as string[]).includes(r.block) && r.block !== 'none'
      ? r.block
      : undefined;
  const position: MatchPosition | undefined =
    typeof r.position === 'string' && (MATCH_POSITIONS as string[]).includes(r.position) && r.position !== 'any'
      ? r.position
      : undefined;
  // Absent replace = matte (match + decorate only). Legacy configs faked matte by
  // setting replace === find, so collapse that (and any non-string) back to matte;
  // a genuine replacement — including '' (replace with blank) — is preserved.
  const replace: string | undefined =
    typeof r.replace === 'string' && r.replace !== r.find ? r.replace : undefined;
  const { replace: _legacyReplace, ...rest } = r;
  return {
    ...rest,
    ...(replace !== undefined ? { replace } : {}),
    style,
    category,
    color,
    block,
    position,
  };
}

// Ensure every seeded default rule exists without clobbering user edits: a
// stored rule keeps its saved shape (normalized) even when its id is a
// default's, so styling / colouring / disabling a default filter survives a
// reload. Only defaults the saved config predates are appended pristine.
function mergeDefaultRules(userRules: FindReplaceRule[]): FindReplaceRule[] {
  const result: FindReplaceRule[] = [];
  const seen = new Set<string>();
  for (const r of userRules) {
    if (!r || typeof r.id !== 'string') continue;
    if (seen.has(r.id)) continue;
    seen.add(r.id);
    result.push(normalizeRule(r));
  }
  for (const def of DEFAULT_RULES) {
    if (!seen.has(def.id)) {
      seen.add(def.id);
      result.push({ ...def });
    }
  }
  return result;
}

function normalizeDateFormat(value: unknown): AppConfig['dateFormat'] {
  if (value === 'YYYY-MM-DD' || value === 'DD MMM YYYY' || value === 'DD.MM.YYYY' || value === 'MM/DD/YYYY') {
    return value;
  }
  return 'YYYY-MM-DD';
}

function migrate(parsed: Record<string, unknown>): AppConfig {
  const base = defaultConfig();
  const rawFeeds = Array.isArray(parsed.feeds) ? parsed.feeds : [];
  const feeds: CalendarFeed[] = [];
  const seenFeedIds = new Set<string>();
  rawFeeds.forEach((f, i) => {
    const normalized = normalizeFeed(f, i);
    // Drop feeds that repeat an id — a duplicate id would collide in events.byFeed
    // and throw `each_key_duplicate` in the feed-row {#each …(feed.id)}.
    if (normalized && !seenFeedIds.has(normalized.id)) {
      seenFeedIds.add(normalized.id);
      feeds.push(normalized);
    }
  });
  if (feeds.length > 0 && !feeds.some((f) => f.id === SCRATCHPAD_FEED_ID)) {
    feeds.push(scratchpadFeed(feeds.length));
  }
  const refreshIntervalMs = snapRefreshInterval(
    typeof parsed.refreshIntervalMs === 'number' ? parsed.refreshIntervalMs : base.refreshIntervalMs,
  );
  const num = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  const rawRules = Array.isArray(parsed.rules) ? (parsed.rules as FindReplaceRule[]) : [];
  return {
    feeds: feeds.length > 0 ? feeds : base.feeds,
    refreshIntervalMs,
    schemaVersion: SCHEMA_VERSION,
    // Legacy configs stored the light/dark value under `theme`; it is now `scheme`.
    scheme: normalizeScheme(parsed.scheme ?? parsed.theme),
    palette: normalizePalette(parsed.palette),
    motion: normalizeMotion(parsed.motion),
    spacing: normalizeSpacing(parsed.spacing),
    traySide: normalizeTraySide(parsed.traySide),
    // Added after schema v3; configs saved before it fetch in Auto mode.
    feedFetch: FEED_FETCH_OPTIONS.includes(parsed.feedFetch as FeedFetch)
      ? (parsed.feedFetch as FeedFetch)
      : base.feedFetch,
    nextEvents: normalizeNextEvents(parsed.nextEvents),
    borderWeight: parsed.borderWeight === 'bold' ? 'bold' : base.borderWeight,
    haptics: normalizeHaptics(parsed.haptics ?? parsed.baptism),
    fontSize: normalizeFontSize(parsed.fontSize),
    // Validated: format.ts indexes month tables by locale and hands the zone to
    // Intl, so a stray value (hand-edited or imported file) would throw on render.
    locale: parsed.locale === 'en' || parsed.locale === 'el' ? parsed.locale : base.locale,
    dateFormat: normalizeDateFormat(parsed.dateFormat),
    rules: mergeDefaultRules(rawRules),
    cardShowDescription:
      typeof parsed.cardShowDescription === 'boolean'
        ? parsed.cardShowDescription
        : base.cardShowDescription,
    cardShowLocation:
      typeof parsed.cardShowLocation === 'boolean'
        ? parsed.cardShowLocation
        : base.cardShowLocation,
    timezone:
      parsed.timezone === 'local' || isValidTimezone(parsed.timezone as string)
        ? (parsed.timezone as AppConfig['timezone'])
        : base.timezone,
    dst: parsed.dst === 'on' || parsed.dst === 'off' ? parsed.dst : base.dst,
    timeFormat: parsed.timeFormat === '12h' ? '12h' : base.timeFormat,
    weekStart: parsed.weekStart === 'sunday' ? 'sunday' : base.weekStart,
    // Added in schema v2: pre-v2 configs lack timezone1 → fall back to Athens.
    timezone1:
      typeof parsed.timezone1 === 'string' && isValidTimezone(parsed.timezone1) ? parsed.timezone1 : base.timezone1,
    timezone2:
      typeof parsed.timezone2 === 'string' && isValidTimezone(parsed.timezone2) ? parsed.timezone2 : base.timezone2,
    // Lower bound matches the smallest fit-24h zoom the week grid can derive
    // on a tall viewport, so a persisted zoomed-out scale isn't snapped back up.
    weekHourScale: Math.min(2, Math.max(0.25, num(parsed.weekHourScale, base.weekHourScale))),
    pastMonths: Math.max(0, Math.round(num(parsed.pastMonths, base.pastMonths))),
    futureMonths: Math.max(0, Math.round(num(parsed.futureMonths, base.futureMonths))),
    morningLimit: typeof parsed.morningLimit === 'string' ? parsed.morningLimit : '',
    eveningLimit: typeof parsed.eveningLimit === 'string' ? parsed.eveningLimit : '',
    trayFilter: (() => {
      const raw = parsed.trayFilter as { categories?: unknown; travel?: unknown } | undefined;
      let cats = Array.isArray(raw?.categories)
        ? (raw.categories as string[]).filter(c => (FEED_CATEGORIES as string[]).includes(c)) as FeedCategory[]
        : base.trayFilter.categories;
      // 'events', 'observances', 'operations' and 'guests' were each added to the tray filter
      // after the original default — backfill them for existing users who had a
      // saved filter, so those events don't silently vanish from the tray.
      if (raw?.categories && !cats.includes('events')) cats = [...cats, 'events'];
      if (raw?.categories && !cats.includes('observances')) cats = [...cats, 'observances'];
      if (raw?.categories && !cats.includes('guests')) cats = [...cats, 'guests'];
      if (raw?.categories && !cats.includes('operations')) cats = [...cats, 'operations'];
      // Travel was folded back into the type filter. A pre-merge config carried a
      // separate `travel` array — map its selections onto the two travel types so
      // previously-shown travel events stay visible (and a deselected travel filter
      // stays hidden). This only fires while the legacy `travel` key is present.
      if (raw && 'travel' in raw) {
        const t = Array.isArray(raw.travel) ? (raw.travel as string[]) : [];
        if (t.includes('local') && !cats.includes('travel-local')) cats = [...cats, 'travel-local'];
        if (t.includes('international') && !cats.includes('travel-international')) {
          cats = [...cats, 'travel-international'];
        }
      }
      return { categories: cats };
    })(),
    // A PIN hash only (kiosk-pin.ts): plain digits from before hashing aren't
    // read, so such a lock drops.
    kioskPin: isPinHash(parsed.kioskPin) ? parsed.kioskPin : null,
  };
}

export function loadConfig(): AppConfig {
  if (typeof localStorage === 'undefined') return defaultConfig();
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return defaultConfig();
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return defaultConfig();
    return migrate(parsed as Record<string, unknown>);
  } catch {
    return defaultConfig();
  }
}

export function saveConfig(config: AppConfig): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export const SETTINGS_SECTIONS_KEY = 'calendar-timeline:settings-sections';

// Filters and Calendars start open (the working sections); the appearance
// groups start collapsed.
function defaultSections(): SettingsSections {
  return { look: false, time: false, filters: true, calendars: true };
}

export function loadSettingsSections(): SettingsSections {
  const sections = defaultSections();
  if (typeof localStorage === 'undefined') return sections;
  const raw = localStorage.getItem(SETTINGS_SECTIONS_KEY);
  if (!raw) return sections;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return sections;
    for (const id of SETTINGS_SECTION_IDS) {
      const v = (parsed as Record<string, unknown>)[id];
      if (typeof v === 'boolean') sections[id] = v;
    }
    return sections;
  } catch {
    return sections;
  }
}

export function saveSettingsSections(sections: SettingsSections): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(SETTINGS_SECTIONS_KEY, JSON.stringify(sections));
}

export const EVENTS_CACHE_KEY = 'calendar-timeline:events';

type SerializedEvent = {
  uid: string;
  feedId: string;
  title: string;
  description: string;
  descriptionSnippet: string;
  location: string;
  start: string;
  end: string;
  allDay: boolean;
  url?: string;
  cancelled?: boolean;
};

// Serializing every event to JSON is synchronous and can block for tens of ms
// on slow devices. Coalesce bursts of refresh writes into a single trailing
// write, with a flush on page hide so nothing is lost on close.
type CachePayload = {
  byFeed: Record<string, ParsedEvent[]>;
  tzByFeed: Record<string, string>;
  lastSuccessAt: Record<string, number>;
  // Per-feed retrieval error messages, persisted so a failed feed's error stays
  // visible across reloads — notably while offline, where the fetch that would
  // re-create the error is skipped.
  feedErrors: Record<string, string>;
  // Per-feed HTTP validators, persisted so the first refresh after a reload can
  // revalidate (304) instead of re-downloading unchanged feeds.
  validators: Record<string, FeedValidators>;
};
let pendingCache: CachePayload | null = null;
let cacheTimer: ReturnType<typeof setTimeout> | null = null;

export function saveEventsCache(
  byFeed: Record<string, ParsedEvent[]>,
  tzByFeed: Record<string, string>,
  lastSuccessAt: Record<string, number>,
  feedErrors: Record<string, string>,
  validators: Record<string, FeedValidators> = {},
): void {
  if (typeof localStorage === 'undefined') return;
  pendingCache = { byFeed, tzByFeed, lastSuccessAt, feedErrors, validators };
  if (cacheTimer) return;
  cacheTimer = setTimeout(flushEventsCache, 1000);
}

export function flushEventsCache(): void {
  if (cacheTimer) {
    clearTimeout(cacheTimer);
    cacheTimer = null;
  }
  if (!pendingCache) return;
  const { byFeed, tzByFeed, lastSuccessAt, feedErrors, validators } = pendingCache;
  pendingCache = null;
  writeEventsCache(byFeed, tzByFeed, lastSuccessAt, feedErrors, validators);
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushEventsCache);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEventsCache();
  });
}

function serializeEventsCache(
  byFeed: Record<string, ParsedEvent[]>,
  tzByFeed: Record<string, string>,
  lastSuccessAt: Record<string, number>,
  feedErrors: Record<string, string>,
  validators: Record<string, FeedValidators>,
): string {
  return JSON.stringify({
    byFeed: Object.fromEntries(
      Object.entries(byFeed)
        // Local lanes (Draft + imported .ics) persist via the scratchpad store,
        // not this network-feed cache; skip them to avoid stale duplicates.
        .filter(([id]) => !id.startsWith('scratchpad:'))
        .map(([id, evts]) => [
        id,
        evts.map((e): SerializedEvent => ({
          uid: e.uid,
          feedId: e.feedId,
          title: e.title,
          description: e.description,
          descriptionSnippet: e.descriptionSnippet,
          location: e.location,
          start: e.start.toISOString(),
          end: e.end.toISOString(),
          allDay: e.allDay,
          ...(e.url ? { url: e.url } : {}),
          ...(e.cancelled ? { cancelled: true } : {}),
        })),
      ]),
    ),
    tzByFeed: { ...tzByFeed },
    lastSuccessAt: { ...lastSuccessAt },
    feedErrors: { ...feedErrors },
    validators: { ...validators },
  });
}

function isQuotaError(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    // Spec name, Firefox's legacy name, and the legacy numeric code (22).
    (err.name === 'QuotaExceededError' ||
      err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      err.code === 22)
  );
}

// The least-recently-refreshed cacheable feed — the first to evict when the
// store is full. Scratchpad lanes aren't in this cache, so they're never picked;
// a feed with no recorded success is treated as oldest.
function oldestCacheableFeedId(
  byFeed: Record<string, ParsedEvent[]>,
  lastSuccessAt: Record<string, number>,
): string | null {
  let oldestId: string | null = null;
  let oldestTs = Infinity;
  for (const id of Object.keys(byFeed)) {
    if (id.startsWith('scratchpad:')) continue;
    const ts = lastSuccessAt[id] ?? 0;
    if (ts < oldestTs) {
      oldestTs = ts;
      oldestId = id;
    }
  }
  return oldestId;
}

function writeEventsCache(
  byFeed: Record<string, ParsedEvent[]>,
  tzByFeed: Record<string, string>,
  lastSuccessAt: Record<string, number>,
  feedErrors: Record<string, string>,
  validators: Record<string, FeedValidators>,
): void {
  if (typeof localStorage === 'undefined') return;
  // Work on a shallow copy so eviction never mutates live app state. On a full
  // store, drop the least-recently-refreshed feed and retry, so the freshest
  // calendars stay cached rather than the whole write failing. The retry count
  // is bounded by the feed count, so a persistently failing store can't spin.
  let feeds = byFeed;
  let errors = feedErrors;
  let valids = validators;
  for (;;) {
    try {
      localStorage.setItem(
        EVENTS_CACHE_KEY,
        serializeEventsCache(feeds, tzByFeed, lastSuccessAt, errors, valids),
      );
      return;
    } catch (err) {
      if (!isQuotaError(err)) return; // unavailable or other error — give up quietly
      const evictId = oldestCacheableFeedId(feeds, lastSuccessAt);
      if (!evictId) return; // nothing left to drop
      const { [evictId]: _dropped, ...rest } = feeds;
      feeds = rest;
      // Drop the evicted feed's error and validators too, so the cache stays
      // internally consistent (no revalidation for a feed whose events are no
      // longer cached — a 304 would leave nothing to show).
      const { [evictId]: _droppedErr, ...restErrors } = errors;
      errors = restErrors;
      const { [evictId]: _droppedVal, ...restValids } = valids;
      valids = restValids;
    }
  }
}

export function loadEventsCache(): {
  byFeed: Record<string, ParsedEvent[]>;
  tzByFeed: Record<string, string>;
  lastSuccessAt: Record<string, number>;
  feedErrors: Record<string, string>;
  validators: Record<string, FeedValidators>;
} | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(EVENTS_CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as {
      byFeed?: Record<string, SerializedEvent[]>;
      tzByFeed?: Record<string, string>;
      lastSuccessAt?: Record<string, number>;
      feedErrors?: Record<string, string>;
      validators?: Record<string, FeedValidators>;
    };
    if (!parsed || typeof parsed !== 'object') return null;
    const byFeed: Record<string, ParsedEvent[]> = {};
    for (const [feedId, evts] of Object.entries(parsed.byFeed ?? {})) {
      if (!Array.isArray(evts)) continue;
      // Local lanes load from the scratchpad store, never this cache; ignore any
      // stale scratchpad entries left by older versions.
      if (feedId.startsWith('scratchpad:')) continue;
      byFeed[feedId] = evts.map((e) => ({
        uid: String(e.uid ?? ''),
        // The cache key is the config feed's id; older caches stamped events
        // with the URL hash instead.
        feedId,
        title: String(e.title ?? ''),
        description: String(e.description ?? ''),
        descriptionSnippet: String(e.descriptionSnippet ?? ''),
        location: String(e.location ?? ''),
        start: new Date(e.start),
        end: new Date(e.end),
        allDay: Boolean(e.allDay),
        ...(e.url ? { url: String(e.url) } : {}),
        ...(e.cancelled === true ? { cancelled: true } : {}),
      }));
    }
    return {
      byFeed,
      tzByFeed:
        typeof parsed.tzByFeed === 'object' && parsed.tzByFeed !== null
          ? (parsed.tzByFeed as Record<string, string>)
          : {},
      lastSuccessAt:
        typeof parsed.lastSuccessAt === 'object' && parsed.lastSuccessAt !== null
          ? (parsed.lastSuccessAt as Record<string, number>)
          : {},
      // Default to {} for caches written before feedErrors existed.
      feedErrors:
        typeof parsed.feedErrors === 'object' && parsed.feedErrors !== null
          ? (parsed.feedErrors as Record<string, string>)
          : {},
      validators: normalizeValidators(parsed.validators),
    };
  } catch {
    return null;
  }
}

// Default to {} for caches written before validators existed; keep only
// well-formed entries (a rangeKey plus at least one validator: a header or the
// body hash).
function normalizeValidators(input: unknown): Record<string, FeedValidators> {
  if (typeof input !== 'object' || input === null) return {};
  const out: Record<string, FeedValidators> = {};
  for (const [feedId, v] of Object.entries(input as Record<string, unknown>)) {
    if (typeof v !== 'object' || v === null) continue;
    const { etag, lastModified, bodyHash, rangeKey } = v as Partial<FeedValidators>;
    if (typeof rangeKey !== 'string') continue;
    if (typeof etag !== 'string' && typeof lastModified !== 'string' && typeof bodyHash !== 'string') continue;
    out[feedId] = {
      rangeKey,
      ...(typeof etag === 'string' ? { etag } : {}),
      ...(typeof lastModified === 'string' ? { lastModified } : {}),
      ...(typeof bodyHash === 'string' ? { bodyHash } : {}),
    };
  }
  return out;
}

// An export file is a full backup: the config plus every local lane's events
// (keyed by feed id) in their stored form — uids and revisions included, so a
// restore is the same lane rather than a copy. Files from before `lanes`
// existed import as config only.
export function exportConfig(config: AppConfig, lanes: Record<string, ParsedEvent[]> = {}): string {
  const out: Record<string, unknown> = { ...config };
  const localIds = new Set(config.feeds.filter((f) => f.source.kind === 'scratchpad').map((f) => f.id));
  const stored = Object.entries(lanes).filter(([id]) => localIds.has(id));
  if (stored.length > 0) {
    out.lanes = Object.fromEntries(stored.map(([id, evts]) => [id, serializeScratchEvents(evts)]));
  }
  return JSON.stringify(out, null, 2);
}

// The local lanes an export file carries, for the local feeds of its (already
// imported) config. Events with unreadable dates are dropped — the file may
// have been edited by hand.
export function importLanes(json: string, config: AppConfig): Record<string, ParsedEvent[]> {
  const parsed = JSON.parse(json) as { lanes?: unknown };
  const lanes = parsed && typeof parsed.lanes === 'object' && parsed.lanes !== null
    ? (parsed.lanes as Record<string, unknown>)
    : {};
  const out: Record<string, ParsedEvent[]> = {};
  for (const feed of config.feeds) {
    if (feed.source.kind !== 'scratchpad' || !(feed.id in lanes)) continue;
    out[feed.id] = deserializeScratchEvents(lanes[feed.id], feed.id).events.filter(
      (e) => !isNaN(e.start.getTime()) && !isNaN(e.end.getTime()),
    );
  }
  return out;
}

export function importConfig(json: string): AppConfig {
  const parsed = JSON.parse(json);
  if (typeof parsed !== 'object' || parsed === null) throw new Error('Invalid config');
  if (!Array.isArray(parsed.feeds)) throw new Error('Invalid feeds');
  const version = typeof parsed.schemaVersion === 'number' ? parsed.schemaVersion : 0;
  // Older backups go through the same migration a stored config does.
  if (version < 1 || version > SCHEMA_VERSION) {
    throw new Error('Unsupported schema version: ' + parsed.schemaVersion);
  }
  return migrate(parsed as Record<string, unknown>);
}
